import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCastContext } from '../context/CastContext';
import { castApi } from '../services/castApi';
import { AgentCreationData } from '../types';
import ThemeSwitcher from '../../../basic/ThemeSwitcher';
import { useTheme } from '../../../basic/ThemeProvider';
import Typography from '../../../basic/Typography';
import { ImagePreview } from './ImagePreview';
import { AudioPreview } from './AudioPreview';
import styles from '../Cast.module.css';

export const AgentCreator: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch } = useCastContext();
  const { isDark, toggleTheme } = useTheme();
  const [formData, setFormData] = useState({
    agent_name: '',
    bio: '',
  });
  const [files, setFiles] = useState({
    headshot_bg: null as File | null,
    control_wav: null as File | null,
  });
  const [previews, setPreviews] = useState({
    headshot_bg: null as string | null,
    control_wav: null as string | null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInputRefs = {
    headshot_bg: useRef<HTMLInputElement>(null),
    control_wav: useRef<HTMLInputElement>(null),
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files: fileList } = e.target;
    if (fileList && fileList[0]) {
      const file = fileList[0];
      setFiles(prev => ({ ...prev, [name]: file }));

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setPreviews(prev => ({ ...prev, [name]: previewUrl }));

      // Clear error when user selects a file
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
  };

  const handleRemoveFile = (fileType: 'headshot_bg' | 'control_wav') => {
    setFiles(prev => ({ ...prev, [fileType]: null }));

    // Clean up preview URL
    if (previews[fileType]) {
      URL.revokeObjectURL(previews[fileType]!);
      setPreviews(prev => ({ ...prev, [fileType]: null }));
    }

    // Clear the file input
    if (fileInputRefs[fileType].current) {
      fileInputRefs[fileType].current!.value = '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.agent_name.trim()) {
      newErrors.agent_name = 'Agent name is required';
    }

    if (!formData.bio.trim()) {
      newErrors.bio = 'Bio is required';
    }

    if (!files.headshot_bg) {
      newErrors.headshot_bg = 'Headshot background image is required';
    } else if (files.headshot_bg.size > 20 * 1024 * 1024) {
      newErrors.headshot_bg = 'Headshot image must be less than 20MB';
    }

    if (!files.control_wav) {
      newErrors.control_wav = 'Control voice file is required';
    } else if (files.control_wav.size > 20 * 1024 * 1024) {
      newErrors.control_wav = 'Control voice file must be less than 20MB';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    dispatch({ type: 'CREATE_AGENT_START' });

    try {
      const creationData: AgentCreationData = {
        agent_name: formData.agent_name,
        bio: formData.bio,
        headshot_bg: files.headshot_bg!,
        control_wav: files.control_wav!,
      };

      const jobStatus = await castApi.createAgent(creationData);
      dispatch({ type: 'CREATE_AGENT_SUCCESS', payload: jobStatus });

      // Navigate to job monitor
      navigate(`/cast/jobs/${jobStatus.job_id}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create agent';
      dispatch({ type: 'CREATE_AGENT_ERROR', payload: errorMessage });
    }
  };

  const handleBack = () => {
    navigate('/cast');
  };

  // Cleanup preview URLs on unmount
  React.useEffect(() => {
    return () => {
      Object.values(previews).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [previews]);

  return (
    <div className={styles['agent-creator']}>
      <div className={styles['creator-header']}>
        <div className={styles['creator-header-left']}>
          <button className={styles['back-button']} onClick={handleBack}>
            ← Back to Agents
          </button>
          <Typography variant="h2" gutterBottom={false}>
            Create New Agent
          </Typography>
        </div>
        <div className={styles['creator-header-right']}>
          <ThemeSwitcher isDark={isDark} onToggle={toggleTheme} />
        </div>
      </div>

      <form className={styles['creator-form']} onSubmit={handleSubmit}>
        <div className={styles['form-section']}>
          <Typography variant="h3" gutterBottom={true}>
            Basic Information
          </Typography>

          <div className={styles['form-group']}>
            <label htmlFor="agent_name">Agent Name *</label>
            <input
              type="text"
              id="agent_name"
              name="agent_name"
              value={formData.agent_name}
              onChange={handleInputChange}
              className={errors.agent_name ? styles.error : ''}
              placeholder="Enter agent name"
            />
            {errors.agent_name && (
              <span className={styles['error-message']}>{errors.agent_name}</span>
            )}
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="bio">Biography *</label>
            <textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleInputChange}
              className={errors.bio ? styles.error : ''}
              placeholder="Enter agent biography"
              rows={4}
            />
            {errors.bio && (
              <span className={styles['error-message']}>{errors.bio}</span>
            )}
          </div>
        </div>

        <div className={styles['form-section']}>
          <Typography variant="h3" gutterBottom={true}>
            Required Assets
          </Typography>

          <div className={styles['form-group']}>
            <label htmlFor="headshot_bg">Headshot Background Image *</label>
            <div className={styles['file-upload-container']}>
              <input
                ref={fileInputRefs.headshot_bg}
                type="file"
                id="headshot_bg"
                name="headshot_bg"
                accept="image/*"
                onChange={handleFileChange}
                className={errors.headshot_bg ? styles.error : ''}
              />
              {files.headshot_bg && (
                <button
                  type="button"
                  className={styles['remove-file-btn']}
                  onClick={() => handleRemoveFile('headshot_bg')}
                >
                  Remove
                </button>
              )}
            </div>
            <small>Maximum file size: 20MB. Supported formats: PNG, JPG, JPEG</small>
            {errors.headshot_bg && (
              <span className={styles['error-message']}>{errors.headshot_bg}</span>
            )}

            {previews.headshot_bg && (
              <ImagePreview
                src={previews.headshot_bg}
                alt="Headshot preview"
                fileName={files.headshot_bg?.name || ''}
                fileSize={files.headshot_bg?.size || 0}
              />
            )}
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="control_wav">Control Voice File *</label>
            <div className={styles['file-upload-container']}>
              <input
                ref={fileInputRefs.control_wav}
                type="file"
                id="control_wav"
                name="control_wav"
                accept="audio/*"
                onChange={handleFileChange}
                className={errors.control_wav ? styles.error : ''}
              />
              {files.control_wav && (
                <button
                  type="button"
                  className={styles['remove-file-btn']}
                  onClick={() => handleRemoveFile('control_wav')}
                >
                  Remove
                </button>
              )}
            </div>
            <small>Maximum file size: 20MB. Supported formats: WAV, MP3</small>
            {errors.control_wav && (
              <span className={styles['error-message']}>{errors.control_wav}</span>
            )}

            {previews.control_wav && (
              <AudioPreview
                src={previews.control_wav}
                fileName={files.control_wav?.name || ''}
                fileSize={files.control_wav?.size || 0}
              />
            )}
          </div>
        </div>

        <div className={styles['form-actions']}>
          <button
            type="button"
            className={styles['cancel-button']}
            onClick={handleBack}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={styles['create-button']}
            disabled={false}
          >
            Create Agent
          </button>
        </div>
      </form>
    </div>
  );
};